import { EditOutlined, FileTextOutlined } from '@ant-design/icons';
import { TextField } from '@refinedev/antd';
import { Button, Form, Input, Skeleton, Tag } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import LinkTextField from '@/components/general/LinkTextField';
import {
  ModalFormActions,
  ShowInfoGrid,
  ShowInfoItem,
} from '@/components/general/ShowInfo/ShowInfo';
import { ShowSection } from '@/components/general/ShowInfo/ShowSection';
import { MACHINES_URLS } from '@/constants/urls';
import { usePermissionContext } from '@/contexts/PermissionContext';
import { SERVICE_ORDER_STATUS, ServiceOrderTypeString } from '@/model/ServiceOrder/ServiceOrder';
import { Ticket } from '@/model/Ticket/Ticket';

import useInlineSectionEdit from './useInlineSectionEdit';

interface ServiceOrderShowWorkDetailProps {
  serviceOrder: any;
  blockEdit: boolean;
  refetch?: () => Promise<any>;
}

const ServiceOrderShowWorkDetail = (props: ServiceOrderShowWorkDetailProps) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissionContext();
  const { serviceOrder, blockEdit, refetch } = props;
  const [isEditing, setIsEditing] = useState(false);
  const { formProps, isSaving, startEditing, handleFinish } = useInlineSectionEdit({
    id: serviceOrder.id,
    record: serviceOrder,
    isEditing,
    setIsEditing,
    refetch: refetch ?? (() => Promise.resolve()),
    successMessage: t('Orden de servicio actualizada correctamente'),
  });
  /**
   * Solo se edita en los estados pendientes de coordinación o coordinada.
   */
  const canEdit =
    !blockEdit &&
    !isEditing &&
    !isSaving &&
    hasPermission('edit') &&
    (serviceOrder.status === SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
      serviceOrder.status === SERVICE_ORDER_STATUS.COORDINADA);
  const editButton = canEdit ? (
    <Button size="small" icon={<EditOutlined />} onClick={startEditing}>
      {t('Editar')}
    </Button>
  ) : undefined;

  if (isEditing) {
    return (
      <ShowSection
        title={t('Detalles del trabajo realizado')}
        className="ServiceOrderShowWorkDetail"
        icon={<FileTextOutlined />}>
        <Form {...formProps} initialValues={serviceOrder} onFinish={handleFinish} layout="vertical">
          <Form.Item
            label={t('Tipo de servicio')}
            name="type"
            rules={[
              {
                required: true,
                message: t('El {{field}} es requerido', { field: t('tipo de servicio') }),
              },
            ]}>
            <AntdSelect
              options={Object.keys(ServiceOrderTypeString).map(type => ({
                label: t(ServiceOrderTypeString[type]),
                value: parseInt(type, 10),
              }))}
              disabled={serviceOrder.created_automatically}
            />
          </Form.Item>
          <Form.Item
            label={t('Descripción')}
            name="description"
            rules={[
              {
                required: true,
                message: t('La {{field}} es requerida', { field: t('descripción') }),
              },
            ]}>
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 6 }} showCount />
          </Form.Item>
          <ModalFormActions onCancel={() => setIsEditing(false)} />
        </Form>
      </ShowSection>
    );
  }

  return (
    <ShowSection
      title={t('Detalles del trabajo realizado')}
      className="ServiceOrderShowWorkDetail"
      headerExtra={editButton}
      icon={<FileTextOutlined />}>
      {isSaving ? (
        <Skeleton active />
      ) : (
        <ShowInfoGrid>
          <ShowInfoItem label={serviceOrder.tickets > 1 ? t('Tickets') : t('Ticket')}>
            <ul>
              {serviceOrder.tickets && serviceOrder.tickets.length > 0 ? (
                serviceOrder.tickets.map((ticket: Ticket | number) =>
                  typeof ticket === 'number' ? (
                    <li key={ticket}>
                      <LinkTextField local to={`/tickets/show/${ticket}`} label={`${ticket}`} />
                    </li>
                  ) : (
                    <li key={ticket.id}>
                      <LinkTextField
                        local
                        to={`/tickets/show/${ticket.id}`}
                        label={`${ticket.ticket_title_str} (${ticket.id.toString()})`}
                      />
                    </li>
                  ),
                )
              ) : (
                <TextField value="-" />
              )}
            </ul>
          </ShowInfoItem>
          <ShowInfoItem
            label={
              serviceOrder.moviles > 1 ? t('Maquinas involucradas') : t('Maquina involucrada')
            }>
            <ul>
              {serviceOrder.moviles && serviceOrder.moviles.length > 0 ? (
                serviceOrder.moviles.map((movil: any) => (
                  <li key={movil.id}>
                    <LinkTextField
                      admin
                      to={`${MACHINES_URLS.ADMIN_MOVIL}/${movil.id}`}
                      label={`${movil.description} (${movil.id})`}
                    />
                  </li>
                ))
              ) : (
                <TextField value="-" />
              )}
            </ul>
          </ShowInfoItem>
          <ShowInfoItem label={t('Tipo')}>
            <Tag>{ServiceOrderTypeString[serviceOrder.type] ?? '-'}</Tag>
          </ShowInfoItem>

          {serviceOrder.delivery_note_links && serviceOrder.delivery_note_links.length > 0 && (
            <ShowInfoItem
              label={
                serviceOrder.delivery_note_links.length > 1
                  ? t('Números de remito')
                  : t('Número de remito')
              }>
              <ul>
                {serviceOrder.delivery_note_links.map((link: string, index: number) => (
                  <li key={link}>
                    <LinkTextField
                      external
                      to={link}
                      label={
                        link.split('source_code=')[1] ?? `${t('Número de remito')} ${index + 1}`
                      }
                    />
                  </li>
                ))}
              </ul>
            </ShowInfoItem>
          )}

          <ShowInfoItem label={t('Descripción')} colSpan={2}>
            <TextField value={serviceOrder.description ?? '-'} />
          </ShowInfoItem>
        </ShowInfoGrid>
      )}
    </ShowSection>
  );
};

export default ServiceOrderShowWorkDetail;
